import React from 'react';

// ─── Stage colors matched to Flutter AuraColors ───────────────────────────
// stageLost = wine (#8A2A3A) · low_budget = saffron · dead = slate

type LostReason =
  | 'dead'
  | 'low_budget'
  | 'competitor'
  | 'irrelevant'
  | 'other'
  | 'not_interested'
  | 'out_of_budget';

interface LostReasonBadgeProps {
  reason: LostReason | string | null;
  competitorName?: string | null;
  freeText?: string | null;
}

const REASON_CONFIG: Record<LostReason, { label: string; bg: string; color: string }> = {
  dead: {
    label: 'Dead — 5 missed',
    bg: 'rgba(122,161,181,0.15)',   // stageCold (#7AA1B5) at 15% opacity
    color: '#4A728A',
  },
  low_budget: {
    label: 'Lost — Budget',
    bg: 'var(--saffron-50, #FBF3DF)',
    color: 'var(--saffron-600, #B87A2E)',
  },
  competitor: {
    label: 'Lost — Competitor',   // overridden with name below
    bg: 'rgba(138,42,58,0.12)',    // wine at 12%
    color: '#8A2A3A',
  },
  irrelevant: {
    label: 'Irrelevant',
    bg: 'rgba(122,161,181,0.15)',
    color: '#4A728A',
  },
  other: {
    label: 'Other',
    bg: 'rgba(138,42,58,0.12)',
    color: '#8A2A3A',
  },
  not_interested: {
    label: 'Not Interested',
    bg: 'rgba(138,42,58,0.12)',
    color: '#8A2A3A',
  },
  out_of_budget: {
    label: 'Out of Budget',
    bg: 'var(--saffron-50, #FBF3DF)',
    color: 'var(--saffron-600, #B87A2E)',
  },
};

const NEUTRAL_CONFIG = {
  bg: 'rgba(139,131,115,0.12)',
  color: 'var(--ink-3, #6B6358)',
};

const truncateDetail = (value: string, maxLength: number) => (
  value.length > maxLength ? `${value.slice(0, maxLength - 2)}…` : value
);

const humanizeReason = (reason: string) => reason
  .trim()
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

export function resolveLostReasonLabel(
  reason: LostReason | string | null,
  competitorName?: string | null,
  freeText?: string | null,
): string {
  if (!reason) return 'Reason not recorded';
  if (reason === 'competitor' && competitorName) return `Lost — ${truncateDetail(competitorName, 14)}`;
  if (reason === 'other' && freeText) return `Other — ${truncateDetail(freeText, 32)}`;
  return REASON_CONFIG[reason as LostReason]?.label ?? humanizeReason(reason);
}

function resolveFullLostReasonLabel(
  reason: LostReason | string | null,
  competitorName?: string | null,
  freeText?: string | null,
): string {
  if (!reason) return 'Reason not recorded';
  if (reason === 'competitor' && competitorName) return `Lost — ${competitorName}`;
  if (reason === 'other' && freeText) return `Other — ${freeText}`;
  return REASON_CONFIG[reason as LostReason]?.label ?? humanizeReason(reason);
}

export function LostReasonBadge({ reason, competitorName, freeText }: LostReasonBadgeProps) {
  const cfg = (reason && REASON_CONFIG[reason as LostReason]) || NEUTRAL_CONFIG;

  const label = resolveLostReasonLabel(reason, competitorName, freeText);
  const fullLabel = resolveFullLostReasonLabel(reason, competitorName, freeText);

  return (
    <span
      aria-label={fullLabel}
      title={fullLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '9999px',
        padding: '2px 8px',
        fontSize: '10px',
        fontWeight: 600,
        lineHeight: 1.4,
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.color}33`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}
